import { CheckIcon, InfoIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import { Input } from "@ui/input";

import { positionCategoryToLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { PositionCategory, SearchV2TenureFilter } from "@/types";

const TENURE_PRESETS: { label: string; floor?: number; ceiling?: number }[] = [
  { label: "Junior", floor: 1, ceiling: 3 },
  { label: "Mid Level", floor: 2, ceiling: 5 },
  { label: "Senior", floor: 5 },
];

const parseYears = (raw: string) => (raw === "" ? undefined : Number(raw));

export const TenureFilter = ({
  onChange,
  value,
}: {
  onChange: (tenure: SearchV2TenureFilter) => void;
  value: SearchV2TenureFilter;
}) => {
  /* Helpers */
  const hasRange = value.floor !== undefined || value.ceiling !== undefined;

  const isPresetSelected = (preset: (typeof TENURE_PRESETS)[number]) =>
    value.floor === preset.floor && value.ceiling === preset.ceiling;

  const formatRange = () => {
    if (value.floor !== undefined && value.ceiling !== undefined) {
      return `${value.floor}–${value.ceiling} years`;
    }
    if (value.floor !== undefined) return `at least ${value.floor} years`;
    return `at most ${value.ceiling} years`;
  };

  /* Handlers */
  const handlePreset = (preset: (typeof TENURE_PRESETS)[number]) => {
    onChange({ ...value, ceiling: preset.ceiling, floor: preset.floor });
  };

  const handleSelectCategory = (category: PositionCategory) => {
    onChange({ ...value, category });
  };

  /* Render */
  const renderPresets = () => {
    return (
      <Row className="flex-wrap items-center gap-2">
        {TENURE_PRESETS.map((preset) => {
          const isSelected = isPresetSelected(preset);
          return (
            <Button
              className={cn(
                "h-7 rounded-full border px-3 text-xs font-medium",
                isSelected
                  ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
              key={preset.label}
              onClick={() => {
                handlePreset(preset);
              }}
              type="button"
              variant="ghost"
            >
              {preset.label}
            </Button>
          );
        })}
      </Row>
    );
  };

  const renderRangeInputs = () => {
    return (
      <Row className="mt-3 w-full gap-4">
        <Column className="w-full gap-1.5">
          <Text className="text-xs text-slate-500">Tenure Floor</Text>
          <Input
            min="0"
            max="40"
            onChange={(event) => {
              onChange({ ...value, floor: parseYears(event.target.value) });
            }}
            placeholder="e.g. 2"
            type="number"
            value={value.floor ?? ""}
          />
        </Column>
        <Column className="w-full gap-1.5">
          <Text className="text-xs text-slate-500">Tenure Ceiling</Text>
          <Input
            min="0"
            max="40"
            onChange={(event) => {
              onChange({ ...value, ceiling: parseYears(event.target.value) });
            }}
            placeholder="e.g. 5"
            type="number"
            value={value.ceiling ?? ""}
          />
        </Column>
      </Row>
    );
  };

  const renderCategories = () => {
    return (
      <Column className="gap-1.5">
        <Text className="text-xs text-slate-500" variant="secondary">
          Count tenure from this position category
        </Text>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Object.values(PositionCategory).map((category) => {
            const isSelected = value.category === category;
            return (
              <Button
                className={cn(
                  "h-auto justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                  isSelected
                    ? "border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-700"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                )}
                key={category}
                onClick={() => {
                  handleSelectCategory(category);
                }}
                type="button"
                variant="ghost"
              >
                {positionCategoryToLabel[category]}
                <Row
                  className={cn(
                    "h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                    isSelected
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-300 bg-white",
                  )}
                >
                  {isSelected ? <CheckIcon className="h-3 w-3" /> : null}
                </Row>
              </Button>
            );
          })}
        </div>
      </Column>
    );
  };

  const renderFilterPreview = () => {
    if (!hasRange) return null;
    return (
      <Row className="items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
        <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        <Column className="gap-1">
          <Text className="text-xs font-medium text-blue-800">
            What this filter matches
          </Text>
          <Text className="text-xs font-normal leading-relaxed text-blue-800">
            Find workers with{" "}
            <span className="font-semibold">{formatRange()}</span> of experience
            in{" "}
            <span className="font-semibold">
              {positionCategoryToLabel[value.category]} roles
            </span>
            .
          </Text>
        </Column>
      </Row>
    );
  };

  const render = () => {
    return (
      <Column className="gap-3">
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tenure Range
        </Text>
        <Column className="gap-1.5">
          <Text className="text-xs text-slate-500" variant="secondary">
            Preset ranges
          </Text>
          {renderPresets()}
          {renderRangeInputs()}
        </Column>
        {renderCategories()}
        {renderFilterPreview()}
      </Column>
    );
  };

  return render();
};
